import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Paper,
  Button,
  Stepper,
  Step,
  StepLabel,
  Divider,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent
} from '@mui/material';
import { useAuth } from '../../../contexts/AuthContext';
import CSVPreview from '../../../components/csv-import/CSVPreview';
import ColumnMapper from '../../../components/csv-import/ColumnMapper';
import { getSupplier } from '../../../services/supplierService';
import { ColumnMapping, Supplier } from '../../../types/supplier';

/**
 * Page de configuration du mapping pour l'importation de carburant
 */
const FuelMappingPage: React.FC = () => {
  const navigate = useNavigate();
  // Nous n'utilisons pas l'authentification pour l'instant
  // const { currentUser, currentOrganization } = useAuth();
  
  const [headers, setHeaders] = useState<string[]>([]);
  const [data, setData] = useState<Record<string, any>[]>([]);
  const [fileName, setFileName] = useState<string>('');
  const [fileSize, setFileSize] = useState<number>(0);
  const [supplierId, setSupplierId] = useState<string>('');
  const [supplierName, setSupplierName] = useState<string>('');
  const [mapping, setMapping] = useState<ColumnMapping>({
    date: '',
    matricule: '',
    typeProduit: '',
    quantite: '',
    montant: '',
    heure: '',
    kilometresAvant: '',
    kilometres: '',
    produit: '',
    lieu: '',
    numFacture: ''
  });
  const [dateFormat, setDateFormat] = useState<string>('DD/MM/YYYY');
  const [saveConfig, setSaveConfig] = useState<boolean>(false);
  const [newSupplierName, setNewSupplierName] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  
  // Étapes de l'importation
  const steps = [
    'Sélection du fichier',
    'Configuration du mapping',
    'Importation des données',
    'Résultats'
  ];
  
  // Formats de date supportés
  const dateFormats = [
    { value: 'DD/MM/YYYY', label: 'JJ/MM/AAAA (31/12/2024)' },
    { value: 'MM/DD/YYYY', label: 'MM/JJ/AAAA (12/31/2024)' },
    { value: 'YYYY-MM-DD', label: 'AAAA-MM-JJ (2024-12-31)' },
    { value: 'DD-MM-YYYY', label: 'JJ-MM-AAAA (31-12-2024)' },
    { value: 'DD.MM.YYYY', label: 'JJ.MM.AAAA (31.12.2024)' }
  ];
  
  // Champs requis pour le mapping
  const requiredFields = [
    { key: 'date', label: 'Date' },
    { key: 'matricule', label: 'Plaque d\'immatriculation' },
    { key: 'typeProduit', label: 'Type de produit' },
    { key: 'quantite', label: 'Quantité' },
    { key: 'montant', label: 'Montant' }
  ];
  
  // Champs optionnels pour le mapping
  const optionalFields = [
    { key: 'heure', label: 'Heure' },
    { key: 'kilometresAvant', label: 'Kilométrage avant' },
    { key: 'kilometres', label: 'Kilométrage' },
    { key: 'produit', label: 'Produit' },
    { key: 'lieu', label: 'Lieu' },
    { key: 'numFacture', label: 'Numéro de facture' }
  ];
  
  // Suggérer un mapping automatique basé sur les en-têtes du fichier CSV
  const suggestMappingFromHeaders = (headers: string[]): Partial<ColumnMapping> => {
    const result: Partial<ColumnMapping> = {};
    const lowerHeaders = headers.map(h => h.toLowerCase());
    
    // Mapping pour la date
    const datePatterns = ['date', 'jour', 'jour_transaction'];
    for (const pattern of datePatterns) {
      const index = lowerHeaders.findIndex(h => h.includes(pattern));
      if (index >= 0) {
        result.date = headers[index];
        break;
      }
    }
    
    // Mapping pour l'heure
    const heurePatterns = ['heure', 'time', 'horodatage'];
    for (const pattern of heurePatterns) {
      const index = lowerHeaders.findIndex(h => h.includes(pattern));
      if (index >= 0) {
        result.heure = headers[index];
        break;
      }
    }
    
    // Mapping pour le matricule
    const matriculePatterns = ['matricule', 'immatriculation', 'plaque', 'vehicule'];
    for (const pattern of matriculePatterns) {
      const index = lowerHeaders.findIndex(h => h.includes(pattern));
      if (index >= 0) {
        result.matricule = headers[index];
        break;
      }
    }
    
    // Mapping pour le kilométrage avant
    const kmAvantPatterns = ['kilometres avant', 'km avant', 'kilometresavant', 'kmavant'];
    for (const pattern of kmAvantPatterns) {
      const index = lowerHeaders.findIndex(h => h.includes(pattern));
      if (index >= 0) {
        result.kilometresAvant = headers[index];
        break;
      }
    }
    
    // Mapping pour le kilométrage
    const kmPatterns = ['kilometres', 'km', 'kilométrage', 'odometre'];
    for (const pattern of kmPatterns) {
      const index = lowerHeaders.findIndex(h => h.includes(pattern));
      if (index >= 0) {
        result.kilometres = headers[index];
        break;
      }
    }
    
    // Mapping pour le type de produit
    const typePatterns = ['type produit', 'type', 'produit type', 'type operation', 'typeoperation'];
    for (const pattern of typePatterns) {
      const index = lowerHeaders.findIndex(h => h.includes(pattern));
      if (index >= 0) {
        result.typeProduit = headers[index];
        break;
      }
    }
    
    // Mapping pour le produit
    const produitPatterns = ['produit', 'article', 'carburant'];
    for (const pattern of produitPatterns) {
      const index = lowerHeaders.findIndex(h => h.includes(pattern));
      if (index >= 0) {
        result.produit = headers[index];
        break;
      }
    }
    
    // Mapping pour la quantité
    const quantitePatterns = ['quantite', 'qte', 'volume', 'litres'];
    for (const pattern of quantitePatterns) {
      const index = lowerHeaders.findIndex(h => h.includes(pattern));
      if (index >= 0) {
        result.quantite = headers[index];
        break;
      }
    }
    
    // Mapping pour le montant
    const montantPatterns = ['montant', 'prix', 'cout', 'total'];
    for (const pattern of montantPatterns) {
      const index = lowerHeaders.findIndex(h => h.includes(pattern));
      if (index >= 0) {
        result.montant = headers[index];
        break;
      }
    }
    
    // Mapping pour le lieu
    const lieuPatterns = ['lieu', 'station', 'site', 'adresse'];
    for (const pattern of lieuPatterns) {
      const index = lowerHeaders.findIndex(h => h.includes(pattern));
      if (index >= 0) {
        result.lieu = headers[index];
        break;
      }
    }
    
    // Mapping pour le numéro de facture
    const facturePatterns = ['facture', 'num facture', 'numero facture', 'reference'];
    for (const pattern of facturePatterns) {
      const index = lowerHeaders.findIndex(h => h.includes(pattern));
      if (index >= 0) {
        result.numFacture = headers[index];
        break;
      }
    }
    
    return result;
  };
  
  // Charger la configuration du fournisseur
  const loadSupplierConfig = async (id: string) => {
    if (!id) return;
    
    try {
      setLoading(true);
      const supplier = await getSupplier(id);
      
      if (supplier) {
        setSupplierName(supplier.name);
        
        if (supplier.csvImportConfig) {
          // Utiliser la configuration CSV existante
          setMapping(supplier.csvImportConfig.columnMapping);
          setDateFormat(supplier.csvImportConfig.dateFormat);
        } else {
          // Suggérer un mapping automatique basé sur les en-têtes
          const suggestedMapping = suggestMappingFromHeaders(headers);
          setMapping(prevMapping => ({
            ...prevMapping,
            ...suggestedMapping
          }));
          console.log('Aucune configuration CSV trouvée pour ce fournisseur. Mapping suggéré automatiquement.');
        }
      }
    } catch (error) {
      console.error('Erreur lors du chargement de la configuration du fournisseur:', error);
      setError('Impossible de charger la configuration du fournisseur.');
    } finally {
      setLoading(false);
    }
  };
  
  // Charger les données du localStorage au chargement de la page
  useEffect(() => {
    const storedHeaders = localStorage.getItem('fuelImport_headers');
    const storedData = localStorage.getItem('fuelImport_data');
    const storedFileName = localStorage.getItem('fuelImport_fileName');
    const storedFileSize = localStorage.getItem('fuelImport_fileSize');
    const storedSupplierId = localStorage.getItem('fuelImport_supplierId');
    
    if (!storedHeaders || !storedData || !storedFileName || !storedFileSize) {
      // Rediriger vers la page d'importation si les données ne sont pas disponibles
      navigate('/admin/fuel/import');
      return;
    }
    
    try {
      setHeaders(JSON.parse(storedHeaders));
      setData(JSON.parse(storedData));
      setFileName(storedFileName);
      setFileSize(parseInt(storedFileSize, 10));
      
      if (storedSupplierId) {
        setSupplierId(storedSupplierId);
        loadSupplierConfig(storedSupplierId);
      } else {
        // Si aucun fournisseur n'est sélectionné, suggérer un mapping automatique
        const parsedHeaders = JSON.parse(storedHeaders);
        const suggestedMapping = suggestMappingFromHeaders(parsedHeaders);
        setMapping(prevMapping => ({
          ...prevMapping,
          ...suggestedMapping
        }));
        console.log('Aucun fournisseur sélectionné. Mapping suggéré automatiquement.');
      }
    } catch (error) {
      console.error('Erreur lors du chargement des données:', error);
      setError('Impossible de charger les données. Veuillez réessayer.');
      navigate('/admin/fuel/import');
    }
  }, [navigate]);
  
  // Gérer le changement de mapping
  const handleMappingChange = (newMapping: Record<string, string>) => {
    // Créer un nouveau mapping en conservant les valeurs existantes
    const updatedMapping = { ...mapping } as Record<string, string>;
    
    // Mettre à jour les valeurs avec celles du nouveau mapping
    Object.entries(newMapping).forEach(([key, value]) => {
      if (key in updatedMapping) {
        updatedMapping[key] = value;
      }
    });
    
    setMapping(updatedMapping as unknown as ColumnMapping);
  };
  
  // Gérer le changement de format de date
  const handleDateFormatChange = (event: SelectChangeEvent<string>) => {
    setDateFormat(event.target.value);
  };
  
  // Gérer le changement de l'option de sauvegarde de la configuration
  const handleSaveConfigChange = (event: SelectChangeEvent<string>) => {
    setSaveConfig(event.target.value === 'true');
  };
  
  // Gérer le changement du nom du nouveau fournisseur
  const handleNewSupplierNameChange = (event: SelectChangeEvent<string>) => {
    setNewSupplierName(event.target.value);
  };
  
  // Vérifier si tous les champs requis sont mappés
  const isValidMapping = () => {
    return requiredFields.every(field => mapping[field.key as keyof ColumnMapping]);
  };
  
  // Gérer le clic sur le bouton "Retour"
  const handleBack = () => {
    navigate('/admin/fuel/import');
  };
  
  // Gérer le clic sur le bouton "Importer les données"
  const handleImport = () => {
    if (!isValidMapping()) {
      setError('Veuillez mapper tous les champs obligatoires.');
      return;
    }
    
    if (saveConfig && !newSupplierName) {
      setError('Veuillez saisir un nom pour le fournisseur.');
      return;
    }
    
    // Stocker la configuration dans le localStorage pour la récupérer dans la page suivante
    localStorage.setItem('fuelImport_mapping', JSON.stringify(mapping));
    localStorage.setItem('fuelImport_dateFormat', dateFormat);
    localStorage.setItem('fuelImport_saveConfig', String(saveConfig));
    
    if (saveConfig) {
      localStorage.setItem('fuelImport_newSupplierName', newSupplierName);
    }
    
    // Rediriger vers la page d'importation des données
    navigate('/admin/fuel/import/process');
  };
  
  return (
    <Container maxWidth="lg">
      <Box sx={{ my: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Configuration du mapping
        </Typography>
        
        <Stepper activeStep={1} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}
        
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Aperçu du fichier
          </Typography>
          
          <Typography variant="body2" color="text.secondary" paragraph>
            Fichier: {fileName} ({(fileSize / 1024).toFixed(2)} Ko)
          </Typography>
          
          <CSVPreview
            headers={headers}
            data={data}
            rowCount={5}
            columnMapping={mapping}
            title="Aperçu des données"
          />
        </Paper>
        
        <ColumnMapper
          headers={headers}
          requiredFields={requiredFields}
          optionalFields={optionalFields}
          initialMapping={mapping as unknown as Record<string, string>}
          onMappingChange={handleMappingChange}
          disabled={loading}
        />
        
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Configuration supplémentaire
          </Typography>
          
          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel id="date-format-label">Format de date</InputLabel>
            <Select
              labelId="date-format-label"
              value={dateFormat}
              onChange={handleDateFormatChange}
              label="Format de date"
              disabled={loading}
            >
              {dateFormats.map((format) => (
                <MenuItem key={format.value} value={format.value}>
                  {format.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          {!supplierId && (
            <>
              <FormControl fullWidth sx={{ mb: 3 }}>
                <InputLabel id="save-config-label">Sauvegarder cette configuration</InputLabel>
                <Select
                  labelId="save-config-label"
                  value={saveConfig ? 'true' : 'false'}
                  onChange={handleSaveConfigChange}
                  label="Sauvegarder cette configuration"
                  disabled={loading}
                >
                  <MenuItem value="false">Non</MenuItem>
                  <MenuItem value="true">Oui</MenuItem>
                </Select>
              </FormControl>
              
              {saveConfig && (
                <FormControl fullWidth sx={{ mb: 3 }}>
                  <InputLabel id="supplier-name-label">Nom du fournisseur</InputLabel>
                  <Select
                    labelId="supplier-name-label"
                    value={newSupplierName}
                    onChange={handleNewSupplierNameChange}
                    label="Nom du fournisseur"
                    disabled={loading}
                  >
                    <MenuItem value="">
                      <em>Sélectionner un nom</em>
                    </MenuItem>
                    <MenuItem value="TOTAL">TOTAL Energies</MenuItem>
                    <MenuItem value="SHELL">Shell</MenuItem>
                    <MenuItem value="BP">BP</MenuItem>
                    <MenuItem value="ESSO">Esso</MenuItem>
                    <MenuItem value="AUTRE">Autre</MenuItem>
                  </Select>
                </FormControl>
              )}
            </>
          )}
          
          {supplierId && (
            <Alert severity="info" sx={{ mb: 3 }}>
              Vous utilisez la configuration du fournisseur <strong>{supplierName}</strong>.
            </Alert>
          )}
        </Paper>
        
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
          <Button
            variant="outlined"
            onClick={handleBack}
            disabled={loading}
          >
            Retour
          </Button>
          
          <Button
            variant="contained"
            onClick={handleImport}
            disabled={!isValidMapping() || loading}
            startIcon={loading ? <CircularProgress size={20} /> : undefined}
          >
            {loading ? 'Chargement...' : 'Importer les données'}
          </Button>
        </Box>
      </Box>
    </Container>
  );
};

export default FuelMappingPage;